/**
 * Source management request schemas (#464).
 *
 * `POST /sources/setup`, `/test` and `/disconnect` — the wire contract of the
 * source wizard. Both sides read these: the edge-api route validates with them,
 * the wizard and the settings connection tab build their bodies by the inferred
 * types instead of string literals.
 *
 * ## Why `vendor` is a plain string here
 *
 * The vendor id domain belongs to the integration catalog in `@aica/core`
 * (`getTelephonyVendorIds()` / `getInstallableVendorIds()`), and `@aica/core`
 * DEPENDS on this package — importing it back would be a cycle. Moving the
 * catalog here is not the answer either: it carries product knowledge (groups,
 * connect kind, capabilities), not wire shapes.
 *
 * So this package owns the SHAPE, and the route narrows the one field whose
 * domain the catalog owns:
 *
 * ```ts
 * const setupVendorSchema = sourceSetupRequestSchema.extend({
 *   vendor: z.enum(TELEPHONY_VENDORS),
 * });
 * ```
 *
 * That keeps both guarantees: the shape is declared once and shared, while a
 * telephony added to the catalog still has to compile against the route.
 */

import { z } from 'zod/v4';

/**
 * What an installation does for the organization: the source of records
 * (default, the behaviour that predates roles) or an audio source only, for a
 * CRM that already owns the record. Parsed server-side by `parseTelephonyRole`.
 */
export const telephonyRoleSchema = z.enum(['ingest', 'media_only']);
export type TelephonyRole = z.infer<typeof telephonyRoleSchema>;

/**
 * `config` stays `Record<string, unknown>` on purpose: per-vendor credential
 * shapes differ, and the route validates them in a second pass keyed by vendor
 * (`setupConfigSchemas`, which lives next to the catalog because it depends on
 * `@aica/connectors` validators).
 */
const vendorConfigSchema = z.record(z.string(), z.unknown());

/** `POST /sources/setup` — create a source installation. */
export const sourceSetupRequestSchema = z.object({
  vendor: z.string().min(1),
  role: telephonyRoleSchema.optional(),
  config: vendorConfigSchema,
});
export type SourceSetupRequest = z.infer<typeof sourceSetupRequestSchema>;

/** `POST /sources/test` — probe credentials without writing anything. */
/**
 * `POST /sources/setup` → 201. `webhookSecret` is present for vendors whose
 * webhook carries a secret (#1403: Unitalk and Ringostat in `Authorization`,
 * Stream Telecom as `?token=` in the URL); `webhookAuthHeader` names the header
 * for the bearer vendors. How each vendor carries it is NOT repeated here — the
 * one declaration is `capabilities.webhookAuth` in the `@aica/core` catalog.
 */
export const sourceSetupResponseSchema = z.object({
  data: z.object({
    installationId: z.string(),
    webhookUrl: z.string(),
    isActive: z.boolean(),
    webhookSecret: z.string().optional(),
    webhookAuthHeader: z.literal('Authorization').optional(),
  }),
});
export type SourceSetupResponse = z.infer<typeof sourceSetupResponseSchema>;

/**
 * `GET /sources/:vendor/webhook-secret` → 200. The secret travels with the
 * installation it belongs to (#1403 review): the selected organization is read
 * at request time, so a caller holding another installation's address must be
 * able to tell the answer is not for it.
 */
export const webhookSecretResponseSchema = z.object({
  data: z.object({
    webhookSecret: z.string(),
    installationId: z.string(),
    webhookUrl: z.string(),
  }),
});
export type WebhookSecretResponse = z.infer<typeof webhookSecretResponseSchema>;

export const sourceTestRequestSchema = z.object({
  vendor: z.string().min(1),
  config: vendorConfigSchema,
});
export type SourceTestRequest = z.infer<typeof sourceTestRequestSchema>;

/**
 * What an installation does for the organization: brings records, or only
 * supplies the recording of a call a CRM record owns. One enum for every
 * response and request that names it — `/sources/active`, the backlog summary
 * and the disconnect body read the same two values.
 */
export const sourceRoleSchema = z.enum(['record_source', 'audio_source']);
export type SourceRole = z.infer<typeof sourceRoleSchema>;

/**
 * `POST /sources/disconnect` — deactivate a source.
 *
 * Three addressing modes, oldest last:
 *
 * - `installationId` — THIS installation, and only if it is still active.
 *   The settings tab caches `GET /sources/active` for 30 s, so a vendor-only
 *   request can land after another administrator replaced the portal with a
 *   new installation of the same vendor: "Disconnect" would then switch off
 *   the replacement rather than what the screen was showing (#465).
 * - `vendor` — whatever installation of that vendor is active right now.
 *   Kept for callers that hold no id.
 * - neither — every active installation. This is what the endpoint meant
 *   before either field existed, and an empty body still means it.
 *
 * `role` — what the screen showed the installation doing, checked against it
 * with `installationId` (#1272). A re-setup in the other role keeps the id, so
 * the id alone let a stale record-source Disconnect switch off the audio
 * source another administrator had made of the same telephony.
 */
export const sourceDisconnectRequestSchema = z.object({
  installationId: z.string().min(1).optional(),
  vendor: z.string().min(1).optional(),
  role: sourceRoleSchema.optional(),
});
export type SourceDisconnectRequest = z.infer<typeof sourceDisconnectRequestSchema>;

/**
 * An installation bringing records right now (#1099). `GET /sources/active`
 * lists every one as `recordSources`: `data` names only the first — the CRM
 * when there is one — and a telephony ingesting next to it was otherwise
 * invisible to the settings screen, which could neither exclude it from «Add
 * telephony as an audio source» nor offer to disconnect it.
 */
export const activeRecordSourceSchema = z.object({
  id: z.string(),
  vendor: z.string(),
});
export type ActiveRecordSource = z.infer<typeof activeRecordSourceSchema>;

/**
 * `reason` of the 409 `POST /sources/setup` answers when the vendor is
 * bringing records and the request would make it an audio-only source (#1099).
 */
export const RECORD_SOURCE_CONFLICT_REASON = 'record_source_conflict' as const;
